import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import { featuresQuery, isHardwareEnabledInMap } from "@/api/queries";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page";
import { DataTable, Td, Th } from "@/components/ui/table";
import { DEVICE_TYPE_LABELS } from "@/features/devices/device-labels";
import { useApiClient } from "@/lib/api-context";
import { EM_DASH } from "@/lib/format";
import { getAppLocale } from "@/i18n";
import { CATALOGUE } from "./catalogue";
import { localizeCatalogueEntry, localizeGatewayCatalogueEntry } from "./catalogue-locale";
import { GATEWAY_CATALOGUE } from "./gateway-catalogue";

/**
 * The hardware this platform supports, one row each — sensors first, then
 * the Semakiot gateways that carry them.
 *
 * A table rather than the full specification of every device stacked down the
 * page: the question a reader arrives with is "which of these do I want",
 * which is a comparison, and a comparison needs columns. Everything that
 * cannot be compared in a cell — what the label says, what the manufacturer
 * claims, what our code does, and what nobody has checked — lives on the
 * detail page behind each row (ADR-049).
 *
 * Two columns carry the honesty this page exists for. **Support** says
 * whether the API would accept the device at all, and **tested** says whether
 * anybody has put a real unit in front of this system. Neither is derived
 * from the other, and as of today the second is "no" for everything.
 *
 * Gateways are a separate section: they are not ingestible device models, so
 * they never share the registration column. Listing names, SKUs and photos
 * come from Semakiot; prices stay unverified.
 */
export function SupportedDevicesPage() {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const features = useQuery(featuresQuery(client));
  const locale = getAppLocale();
  const enabledHardware = features.data?.enabledHardware;
  const devices = CATALOGUE.filter((entry) =>
    isHardwareEnabledInMap(enabledHardware, entry.id),
  ).map((entry) => localizeCatalogueEntry(entry, locale));
  const gateways = GATEWAY_CATALOGUE.map((entry) =>
    localizeGatewayCatalogueEntry(entry, locale),
  );
  const untested = devices.filter((entry) => !entry.verifiedOnHardware).length;

  return (
    <>
      <PageHeader
        title={t("supportedTitle")}
        description={t("supportedDescription")}
      />

      <Card>
        <DataTable caption={t("supportedTitle")}>
          <thead>
            <tr>
              <Th className="w-16">{t("colPhoto")}</Th>
              <Th>{t("colDevice")}</Th>
              <Th>{t("colMeasures")}</Th>
              <Th>{t("colSoldAs")}</Th>
              <Th>{t("colSupport")}</Th>
              <Th>{t("colTestedOnHardware")}</Th>
            </tr>
          </thead>

          <tbody>
            {devices.map((entry) => (
              <tr key={entry.id}>
                <Td>
                  <img
                    src={entry.imageSrc}
                    alt=""
                    width={48}
                    height={48}
                    className="size-12 rounded-md bg-surface-muted object-contain p-0.5"
                    loading="lazy"
                  />
                </Td>
                <Td>
                  <Link
                    to={`/supported-devices/${entry.id}`}
                    className="font-medium text-brand underline underline-offset-2"
                  >
                    {entry.manufacturer} {entry.model}
                  </Link>
                </Td>

                <Td className="text-ink-muted">
                  {/*
                   * A pending device has no device type, because there is no
                   * registry entry to take one from. Writing one here would
                   * claim a measurement the platform cannot store.
                   */}
                  {entry.deviceType ? DEVICE_TYPE_LABELS[entry.deviceType] : EM_DASH}
                </Td>

                <Td className="text-ink-muted">
                  {entry.listingName}
                  <span className="block text-xs">{t("partSku", { sku: entry.sku })}</span>
                </Td>

                <Td>
                  {entry.pending ? (
                    <Badge tone="neutral">{t("supportPending")}</Badge>
                  ) : (
                    <Badge tone="info">{t("supportReady")}</Badge>
                  )}
                </Td>

                <Td>
                  {entry.verifiedOnHardware ? (
                    <Badge tone="ok">{t("testedYes")}</Badge>
                  ) : (
                    <Badge tone="warn">{t("testedNo")}</Badge>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </DataTable>

        {untested > 0 ? (
          <CardBody className="border-t border-border-subtle text-sm text-ink-muted">
            {untested === devices.length
              ? t("supportedUntestedAll")
              : t("supportedUntestedSome", { count: untested })}{" "}
            {t("supportedNoPrice")}
          </CardBody>
        ) : null}
      </Card>

      <div className="mt-10">
        <PageHeader
          title={t("supportedGatewaysTitle")}
          description={t("supportedGatewaysDescription")}
        />
      </div>

      <Card>
        <DataTable caption={t("supportedGatewaysTitle")}>
          <thead>
            <tr>
              <Th className="w-16">{t("colPhoto")}</Th>
              <Th>{t("colGateway")}</Th>
              <Th>{t("colPlacement")}</Th>
              <Th>{t("colSoldAs")}</Th>
            </tr>
          </thead>
          <tbody>
            {gateways.map((entry) => (
              <tr key={entry.id}>
                <Td>
                  <img
                    src={entry.imageSrc}
                    alt=""
                    width={48}
                    height={48}
                    className="size-12 rounded-md bg-surface-muted object-contain p-0.5"
                    loading="lazy"
                  />
                </Td>
                <Td>
                  <p className="font-medium text-ink">
                    {entry.manufacturer} {entry.model}
                  </p>
                  <p className="mt-1 max-w-md text-xs text-ink-muted">{entry.summary}</p>
                  <a
                    href={entry.listingUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="mt-1 inline-block text-xs font-medium text-brand-strong underline underline-offset-2"
                  >
                    {t("supportedGatewayListing")}
                  </a>
                </Td>
                <Td className="text-ink-muted">
                  {entry.placement === "indoor"
                    ? t("gatewayPlacementIndoor")
                    : t("gatewayPlacementOutdoor")}
                </Td>
                <Td className="text-ink-muted">
                  {entry.listingName}
                  <span className="block text-xs">{t("partSku", { sku: entry.sku })}</span>
                </Td>
              </tr>
            ))}
          </tbody>
        </DataTable>
        <CardBody className="border-t border-border-subtle text-sm text-ink-muted">
          {t("supportedGatewaysFootnote")}
        </CardBody>
      </Card>
    </>
  );
}
